import { useEffect, useRef, useState } from 'react'
import { injectIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { ModalHeader } from '@vibialighting/ui-kit'
import _ from 'lodash'

import Config from '@/config'
import { useCatalog } from "@/hooks"
import { getURL } from '@/utils'
import { authActions, errorActions, supportActions, uiActions } from '@/redux/actions'
import { AUTH_VIEWS } from '@/constants'
import userStatus from '@/constants/userStatus.constants'
import { useModalContext } from '@/contexts/ModalContext'
import { LANGUAGE_LOCALES_CODE } from '@/constants'
import Toggles from '@domain/toggles/Toggles'

const ModalAuth = (props) => {
	const {
		intl: { formatMessage, locale },
		authView = 'login',
		authViewParams = {},
		language,
		onAuth,
		onClose,
		authViewState = {}
	} = props

	const { modalUnmount } = useModalContext()
	const dispatch = useDispatch()
	const location = useLocation()
	const { isUsaCatalog } = useCatalog()
	const [title, setTitle] = useState('')
	const [activeView, setActiveView] = useState(null)

	const iframeRef = useRef(null)
	const [fullScreenModal, setFullScreenModal] = useState(window.innerWidth < 575)

	const frAuthPath = Config.getInstance().getConfig('endpoints.fr-auth.url')
	const frAuthSubdomain = Config.getInstance().getConfig('endpoints.fr-auth.subdomain')
	const frAuthUrl = getURL(frAuthPath, frAuthSubdomain)

	const paramsString = Object.entries(authViewParams).reduce((string, [key, value]) => {
		if (value === undefined || key === 'language') {
			return string
		}
		return `${string}&${key}=${value}`
	}, `language=${authViewParams.language || LANGUAGE_LOCALES_CODE[locale]}`)

	const viewUrl = frAuthUrl + "/" + (AUTH_VIEWS[authView] || '') + (paramsString ? `?${paramsString}` : '')

	const handleClose = () => {
		let forceRerender = false
		typeof onClose === 'function' && onClose()
		dispatch(authActions.showModalAuth(false))
		if (Toggles.isEnabled("showDiferentBannersForAssistedCpProducts") && activeView == 'support' && authViewParams.siteLocation == 'TSSAlert') {
			forceRerender = true
		}
		modalUnmount(forceRerender)
		switch (activeView) {
			case 'login':
				dispatch(authActions.modalAuthClosed())
				break
			case 'signUp':
				dispatch(authActions.signUpSocialError())
				break
			case 'qualification':
				dispatch(authActions.qualifiedError({ message: 'modal closed' }))
				break
			case 'support':
				dispatch(supportActions.supportRequestedFailure({ message: 'modal closed' }))
				break
		}
	}

	const handleAuth = async (e) => {
		if (e.origin !== frAuthUrl) {
			return
		}

		if (e.data.id === 'vibia.auth.signUp') {
			dispatch(authActions.signUpSuccess(e.data.payload))
		}

		if (e.data.id === 'login.social.success.credentials') {
			dispatch(authActions.signUpSocialSuccess(e.data.payload))
		}

		if (e.data === 'vibia.auth.logged') {
			typeof onAuth === 'function' && onAuth()
		}

		if (e.data.id === 'vibia.auth.qualified.success') {
			try {
				await dispatch(authActions.getAccountDetails())
				dispatch(authActions.qualifiedSuccess())
			} catch (e) {
				dispatch(errorActions.setError('Something wrong happened'))
			}
		}

		if (e.data.id === 'vibia.auth.qualified.error') {
			dispatch(authActions.qualifiedError(e.data.payload))
		}

		if (e.data.id === 'vibia.supportRequested.success') {
			dispatch(supportActions.supportRequestedSuccess(e.data.payload))
		}

		if (e.data.id === 'vibia.supportRequested.error') {
			dispatch(supportActions.supportRequestedFailure(e.data.payload))
		}

		if (e.data === 'vibia.auth.close') {
			dispatch(authActions.showModalAuth(false))
			modalUnmount()
		}

		if (e.data === 'vibia.auth.loginTimeout') {
			dispatch(authActions.loginTimeout())
		}

		if (e.data === 'vibia.auth.ready') {
			handleFullScreenMessage(fullScreenModal)
			handleStateMessage()
		}

		if (/^vibia.jwtToken./.test(e.data)) {
			const token = e.data.substring(15)
			dispatch(authActions.saveAuthToken(token))
		}

		if (/^vibia.project_budget./.test(e.data)) {
			const { projectId, siteLocation } = JSON.parse(e.data.substring(21))

			await dispatch(authActions.getAccountDetails())

			dispatch(
				uiActions.navigateTo({
					path: isUsaCatalog()
						? `/project/${projectId}/usa-request-budget`
						: `/project/${projectId}/request-budget?continue=${location.pathname}`,
					requiredUserStatus: userStatus.QUALIFIED,
					state: {
						siteLocation
					}
				})
			)
			dispatch(authActions.showModalAuth(false))
			modalUnmount()
		}

		if (e.data.id === 'vibia.support.form.error') {
			dispatch(supportActions.supportRequestedFailure(e.data.payload))
		}

		if (e.data.id === 'login.message.credentials') {
			dispatch(authActions.supportCredentialsError(e.data.payload))
		}

		if (e.data.id === 'login.social.error.credentials') {
			dispatch(authActions.signUpSocialError(e.data.payload))
		}

	}

	const handleMessage = (e) => {
		if (e.origin !== frAuthUrl) {
			return
		}
		switch (e.data?.type) {
			case 'VIEW_LOADED':
				setTitle(`modal.${e.data.payload}.title`)
				setActiveView(e.data.payload)
				break
		}
	}

	useEffect(() => {
		window.addEventListener('message', handleAuth)
		return () => {
			window.removeEventListener('message', handleAuth)
		}
	}, [])

	useEffect(() => {
		window.addEventListener('message', handleMessage)
		return () => {
			window.removeEventListener('message', handleMessage)
		}
	}, [])

	useEffect(() => {
		const mqlFullScreenModal = window.matchMedia('(max-width: 575px)')
		mqlFullScreenModal.addListener(handleFullScreenModal)
		return () => mqlFullScreenModal.removeListener(handleFullScreenModal)
	}, [])

	useEffect(() => {
		handleFullScreenMessage(fullScreenModal)
	}, [fullScreenModal])

	useEffect(() => {
		handleStateMessage()
	}, [authViewState])

	const handleFullScreenModal = (mdlEvent) => {
		setFullScreenModal(mdlEvent.matches)
	}

	const handleFullScreenMessage = (fullScreenModal) => {
		iframeRef?.current &&
			iframeRef.current.contentWindow.postMessage(
				`vibia.screen.${fullScreenModal ? 'fullScreenModal' : 'defaultModal'}`,
				'*'
			)
	}

	const handleStateMessage = () => {
		if (!_.isEmpty(authViewState)) {
			setTimeout(() => {
				iframeRef?.current &&
					iframeRef.current.contentWindow.postMessage(`vibia.support.state=${JSON.stringify(authViewState)}`, '*')
				iframeRef?.current &&
					iframeRef.current.contentWindow.postMessage({ type: 'SUPPORT_STATE', payload: authViewState }, '*')
			})
		}
	}

	return (
		<>
			{authViewParams.siteLocation === 'NewRegister' ? (
				<ModalHeader onClose={() => {}} style={{ paddingLeft: '2rem' }}>
					<img src="/vibia-logo.png" alt="Vibia" />
				</ModalHeader>
			) : (
				<ModalHeader onClose={handleClose} data-qa="modalAuthHeader">
					{title ? formatMessage({ id: title }) : formatMessage({ id: 'title.loading' })}
				</ModalHeader>
			)}
			<iframe
				src={viewUrl}
				frameBorder="0"
				name="authIframe"
				ref={iframeRef}
				style={{
					border: '0px solid white',
					height: '79vh',
					width: '100%'
				}}
			/>
		</>
	)
}

export default injectIntl(ModalAuth)
